import React, { useState } from 'react'
import { Box, IconButton, Typography, Avatar, Button, Stack, Chip, Switch } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import LogoutIcon from '@mui/icons-material/Logout'
import TuneIcon from '@mui/icons-material/Tune'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '../context/authcontext'
import { useScan } from '../context/scanContext'
import { ScanModes } from '../utils/scanModeEnum'
import AppShell from '../components/layout/AppShell'
import { Surface, InfoRow } from '../components/ui/Surface'
import {
  isSoundEnabled,
  setSoundEnabled,
  playValidSound,
} from '../services/soundService'

const AccountView = () => {
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { scanMode, controlPoint } = useScan()
  const [soundOn, setSoundOn] = useState(isSoundEnabled)

  const handleToggleSound = (event) => {
    const enabled = event.target.checked
    setSoundOn(enabled)
    setSoundEnabled(enabled)
    // Aperçu immédiat : l'employé entend le volume réel de l'appareil.
    if (enabled) playValidSound()
  }

  const isAdmin = user?.Admin === 'true' || user?.Admin === true

  const initials = [user?.FirstName, user?.LastName]
    .filter(Boolean)
    .map((name) => name[0]?.toUpperCase())
    .join('')

  const eventName = (() => {
    try {
      return JSON.parse(user?.Event?.Name).fr
    } catch {
      return user?.Event?.Name
    }
  })()

  return (
    <AppShell
      title="Réglages"
      leading={
        <IconButton onClick={() => navigate('/scan')} aria-label="Retour" edge="start">
          <ArrowBackIcon />
        </IconButton>
      }
      footer={
        <Stack spacing={1}>
          <Button
            fullWidth
            variant="outlined"
            color="inherit"
            size="large"
            startIcon={<TuneIcon />}
            onClick={() => navigate('/mode')}
            disabled={!isAdmin}
          >
            Changer le mode
          </Button>
          <Button
            fullWidth
            variant="contained"
            color="error"
            size="large"
            startIcon={<LogoutIcon />}
            onClick={logout}
          >
            Déconnexion
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2.5}>
        <Surface>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                width: 60,
                height: 60,
                bgcolor: 'primary.main',
                fontWeight: 800,
                fontSize: '1.25rem',
              }}
            >
              {initials || '?'}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" noWrap>
                {[user?.FirstName, user?.LastName].filter(Boolean).join(' ') || 'Utilisateur'}
              </Typography>
              {isAdmin ? (
                <Chip size="small" color="primary" label="Administrateur" sx={{ mt: 0.75 }} />
              ) : (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Personnel de contrôle
                </Typography>
              )}
            </Box>
          </Stack>
        </Surface>

        <Box>
          <Typography variant="overline" sx={{ display: 'block', mb: 1, px: 0.5 }}>
            Configuration active
          </Typography>
          <Surface>
            <InfoRow label="Événement" value={eventName} />
            <InfoRow
              label="Mode"
              value={
                scanMode === ScanModes.ACCESS
                  ? "Contrôle d'accès"
                  : scanMode === ScanModes.PRODUCT
                  ? 'Consommation de produits'
                  : 'Non configuré'
              }
            />
            {scanMode === ScanModes.ACCESS ? (
              <InfoRow label="Point de contrôle" value={controlPoint?.Name} />
            ) : null}
          </Surface>
        </Box>

        <Box>
          <Typography variant="overline" sx={{ display: 'block', mb: 1, px: 0.5 }}>
            Préférences
          </Typography>
          <Surface>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
              component="label"
              sx={{ cursor: 'pointer' }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1">Son de validation</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Signal sonore distinct à chaque billet accepté ou refusé.
                </Typography>
              </Box>
              <Switch
                checked={soundOn}
                onChange={handleToggleSound}
                inputProps={{ 'aria-label': 'Son de validation' }}
              />
            </Stack>
          </Surface>
        </Box>

        {!isAdmin ? (
          <Typography variant="caption" sx={{ color: 'text.secondary', px: 0.5 }}>
            Seul un administrateur peut modifier le mode de cet appareil.
          </Typography>
        ) : null}
      </Stack>
    </AppShell>
  )
}

export default AccountView
